/**
 * MessageActionSheet Component
 *
 * Bottom sheet with context actions for chat messages.
 * - Delete (own messages only)
 * - Report (others' messages)
 *
 * Issue #920: Add UI for message deletion, user blocking, and reporting
 * Issue #1222: Optimistic update to immediately remove deleted messages
 */

import { useState } from 'react';
import { Trash2, Flag, X, AlertTriangle, Pin, PinOff } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useOroMutation, OroOperation } from '../hooks/useOroMutation';
// #2674: reuse the REAL page shape (carries `pinnedAnnouncement`) rather than a
// local subset — the optimistic delete below must clear that field, and a
// drifted local copy that omitted it is exactly how the deleted-pin bug shipped.
import {
  useToggleReaction,
  usePinMessage,
  useUnpinMessage,
  type MessagesPage,
} from '../hooks/useRoomMessages';
import { messagesApi, type MessageReaction, type ReactionEmoji } from '../lib/api/messages';
import { reportsApi, type CreateReportPayload } from '../lib/api/reports';
import Button from './Button';
import ReactionPalette from './ReactionPalette';

interface MessageActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  messageId: string;
  clubId: string;
  roomId: string; // #1222: For query invalidation
  senderId: string;
  senderName?: string;
  isOwnMessage: boolean;
  messagePreview?: string;
  // #989: this message's current reactions (drives the palette's own-highlight
  // and correct toggle direction). Defaults to [].
  reactions?: MessageReaction[];
  // #989: whether the viewer may react (room write access + server-id message).
  canReact?: boolean;
  // #2708: leader/owner pin capability hint (can_pin). Gates the Pin/Unpin row.
  canPin?: boolean;
  // #2708: true when this message is the room's current pin (Pin↔Unpin).
  isPinnedMessage?: boolean;
}

type ReportReason = CreateReportPayload['reason'];

export default function MessageActionSheet({
  isOpen,
  onClose,
  messageId,
  clubId,
  roomId,
  senderId,
  senderName,
  isOwnMessage,
  messagePreview,
  reactions = [],
  canReact = false,
  canPin = false,
  isPinnedMessage = false,
}: MessageActionSheetProps) {
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportReason, setReportReason] = useState<ReportReason>('inappropriate_content');
  const [reportNotes, setReportNotes] = useState('');

  // #989 reaction toggle (from the sheet palette row). Optimistic + rollback
  // live in the shared hook; the sheet just fires with the correct direction.
  const toggleReaction = useToggleReaction(roomId);
  const mineSet = new Set<ReactionEmoji>(reactions.filter((r) => r.mine).map((r) => r.emoji));
  const handleReact = (emoji: ReactionEmoji) => {
    const mine = reactions.find((r) => r.emoji === emoji)?.mine ?? false;
    toggleReaction.mutate({ messageId, emoji, mine });
    onClose();
  };

  // #2708 pin/unpin (leader/owner only; server-enforced, UI-gated on canPin).
  const pinMessage = usePinMessage(roomId);
  const unpinMessage = useUnpinMessage(roomId);
  const handlePin = () => {
    pinMessage.mutate({ messageId });
    onClose();
  };
  const handleUnpin = () => {
    unpinMessage.mutate();
    onClose();
  };

  // Delete mutation with optimistic update (#1222)
  const deleteMutation = useOroMutation<
    { ok: boolean },
    { clubId: string; messageId: string },
    { previousData: InfiniteData<MessagesPage> | undefined }
  >({
    mutationFn: ({ clubId, messageId }) => messagesApi.deleteMessage(clubId, messageId),
    oro: { op: OroOperation.DeleteMessage },
    successToast: 'Message deleted',
    onMutate: async ({ messageId }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['room-messages', { roomId }] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<InfiniteData<MessagesPage>>(['room-messages', { roomId }]);

      // Optimistically remove the message from the cache. #2674: also clear a
      // page's cached server `pinnedAnnouncement` when IT is the deleted message
      // — otherwise `pickPinnedAnnouncement` keeps resolving the just-deleted
      // announcement (the server candidate is independent of the message list)
      // and the banner points at a row no longer rendered, until the settle
      // invalidation refetch lands. `null` = "no server pin as of this view";
      // the full-snapshot rollback below restores it on error.
      // #2708 (Frank r1): the TRUE pin (`pinnedMessage`) needs the identical
      // clear — ClubChatPage derives its persistent pin banner directly from
      // this field, and the backend delete path NULLs `rooms.pinned_message_id`
      // before the soft delete (`clubs.ts` pin guard 1), so the post-delete
      // truth is "no true pin". Without this, the banner keeps rendering and
      // linking to the deleted message until the settle invalidation lands.
      if (previousData) {
        queryClient.setQueryData<InfiniteData<MessagesPage>>(['room-messages', { roomId }], {
          ...previousData,
          pages: previousData.pages.map(page => ({
            ...page,
            messages: page.messages.filter(m => m.id !== messageId),
            pinnedAnnouncement:
              page.pinnedAnnouncement?.id === messageId ? null : page.pinnedAnnouncement,
            pinnedMessage:
              page.pinnedMessage?.id === messageId ? null : page.pinnedMessage,
          })),
        });
      }

      // Close the sheet immediately for better UX
      onClose();

      return { previousData };
    },
    onError: (_err, _variables, context) => {
      // Roll back on error
      if (context?.previousData) {
        queryClient.setQueryData(['room-messages', { roomId }], context.previousData);
      }
    },
    onSettled: () => {
      // Refetch to ensure consistency with server
      queryClient.invalidateQueries({ queryKey: ['room-messages', { roomId }] });
    },
  });

  // Report mutation
  const reportMutation = useOroMutation<{ success: boolean; message: string; report_id: string }, CreateReportPayload>({
    mutationFn: (payload) => reportsApi.createReport(payload),
    oro: { op: OroOperation.ReportUser },
    successToast: 'Report submitted. Our team will review it.',
    onSuccess: () => {
      setShowReportForm(false);
      setReportReason('inappropriate_content');
      setReportNotes('');
      onClose();
    },
  });

  const handleDelete = () => {
    deleteMutation.mutate({ clubId, messageId });
  };

  const handleReport = () => {
    if (!reportReason) {
      toast.error('Please select a reason');
      return;
    }

    reportMutation.mutate({
      reported_id: senderId,
      context_type: 'message',
      context_id: messageId,
      reason: reportReason,
      notes: reportNotes || undefined,
    });
  };

  const handleClose = () => {
    setShowDeleteConfirm(false);
    setShowReportForm(false);
    setReportReason('inappropriate_content');
    setReportNotes('');
    onClose();
  };

  if (!isOpen) return null;

  const reportReasons: { value: ReportReason; label: string }[] = [
    { value: 'spam', label: 'Spam or advertising' },
    { value: 'harassment', label: 'Harassment or bullying' },
    { value: 'hate_speech', label: 'Hate speech' },
    { value: 'safety_concern', label: 'Safety concern' },
    { value: 'inappropriate_content', label: 'Inappropriate content' },
    { value: 'other', label: 'Other concern' },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        style={{ zIndex: 50 }}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Bottom Sheet */}
      <div
        className="fixed inset-x-0 bottom-0 bg-oro-surface border-t border-oro-border rounded-t-2xl shadow-floating animate-in slide-in-from-bottom duration-200"
        style={{ zIndex: 60 }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="action-sheet-title"
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-oro-border rounded-full" />
        </div>

        {/* Delete Confirmation View */}
        {showDeleteConfirm && (
          <div className="px-4 pb-8 pt-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-oro-error/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-oro-error" />
              </div>
              <div>
                <h3 id="action-sheet-title" className="font-semibold text-text-primary">
                  Delete Message?
                </h3>
                <p className="text-sm text-text-secondary">This cannot be undone.</p>
              </div>
            </div>

            {messagePreview && (
              <div className="bg-oro-surface-alt border border-oro-border rounded-lg p-3 mb-4">
                <p className="text-sm text-text-secondary line-clamp-2">{messagePreview}</p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="secondary"
                size="md"
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="md"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="flex-1"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        )}

        {/* Report Form View */}
        {showReportForm && (
          <div className="px-4 pb-8 pt-2">
            <div className="flex items-center justify-between mb-4">
              <h3 id="action-sheet-title" className="font-semibold text-text-primary">
                Report Message
              </h3>
              <button
                onClick={() => setShowReportForm(false)}
                className="p-2 -mr-2 text-text-secondary hover:text-text-primary transition-colors"
                aria-label="Back"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {senderName && (
              <p className="text-sm text-text-secondary mb-4">
                Reporting message from <span className="text-text-primary">{senderName}</span>
              </p>
            )}

            {/* Reason Selection */}
            <div className="space-y-2 mb-4">
              <label className="text-sm font-medium text-text-primary">Reason</label>
              <div className="space-y-2">
                {reportReasons.map((reason) => (
                  <label
                    key={reason.value}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      reportReason === reason.value
                        ? 'border-accent bg-accent/5'
                        : 'border-oro-border hover:border-oro-border-hover'
                    }`}
                  >
                    <input
                      type="radio"
                      name="reportReason"
                      value={reason.value}
                      checked={reportReason === reason.value}
                      onChange={(e) => setReportReason(e.target.value as ReportReason)}
                      className="sr-only"
                    />
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        reportReason === reason.value ? 'border-accent' : 'border-oro-border'
                      }`}
                    >
                      {reportReason === reason.value && (
                        <div className="w-2 h-2 rounded-full bg-accent" />
                      )}
                    </div>
                    <span className="text-text-primary">{reason.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Additional Notes */}
            <div className="mb-4">
              <label className="text-sm font-medium text-text-primary block mb-2">
                Additional details (optional)
              </label>
              <textarea
                value={reportNotes}
                onChange={(e) => setReportNotes(e.target.value)}
                placeholder="Tell us more about the issue..."
                className="w-full bg-oro-surface-alt border border-oro-border rounded-lg p-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none resize-none"
                rows={3}
              />
            </div>

            <Button
              variant="primary"
              size="md"
              onClick={handleReport}
              disabled={reportMutation.isPending}
              className="w-full"
            >
              {reportMutation.isPending ? 'Submitting...' : 'Submit Report'}
            </Button>
          </div>
        )}

        {/* Main Actions View */}
        {!showDeleteConfirm && !showReportForm && (
          <div className="px-4 pb-8 pt-2">
            <h3 id="action-sheet-title" className="sr-only">
              Message Actions
            </h3>

            {/* #989: reaction palette row (top of the sheet). One of the three
                Rule 20 carve-out surfaces (picker palette). */}
            {canReact && (
              <div className="mb-2 border-b border-oro-border px-1 pb-3">
                <ReactionPalette
                  mine={mineSet}
                  disabled={toggleReaction.isPending}
                  onSelect={handleReact}
                />
              </div>
            )}

            <div className="space-y-1">
              {/* #2708: leader/owner-only pin row (rendered from the can_pin
                  hint; the mutation is server-enforced). Pin ↔ Unpin. */}
              {canPin && (
                isPinnedMessage ? (
                  <button
                    onClick={handleUnpin}
                    disabled={unpinMessage.isPending}
                    className="flex items-center gap-3 w-full p-4 rounded-lg text-text-primary hover:bg-oro-surface-alt transition-colors disabled:opacity-60"
                  >
                    <PinOff className="w-5 h-5" />
                    <span className="font-medium">Unpin message</span>
                  </button>
                ) : (
                  <button
                    onClick={handlePin}
                    disabled={pinMessage.isPending}
                    className="flex items-center gap-3 w-full p-4 rounded-lg text-text-primary hover:bg-oro-surface-alt transition-colors disabled:opacity-60"
                  >
                    <Pin className="w-5 h-5" />
                    <span className="font-medium">Pin message</span>
                  </button>
                )
              )}
              {isOwnMessage ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-3 w-full p-4 rounded-lg text-oro-error hover:bg-oro-error/10 transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                  <span className="font-medium">Delete Message</span>
                </button>
              ) : (
                <button
                  onClick={() => setShowReportForm(true)}
                  className="flex items-center gap-3 w-full p-4 rounded-lg text-text-primary hover:bg-oro-surface-alt transition-colors"
                >
                  <Flag className="w-5 h-5" />
                  <span className="font-medium">Report Message</span>
                </button>
              )}

              <button
                onClick={handleClose}
                className="flex items-center justify-center w-full p-4 rounded-lg text-text-secondary hover:bg-oro-surface-alt transition-colors"
              >
                <span>Cancel</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
